import router from 'next/router'
import { useState } from 'react'
import useMemoizedPathFor from '../../lib/pathFor'
import useGroup from '../../lib/useGroup'
import { useLeavePageConfirmation } from '../../lib/useLeavePageConfirmation'
import usePopoutTriggerRef from '../../lib/usePopoutTriggerRef'
import usePostCrud from '../../lib/usePostCrud'
import useReplyCrud from '../../lib/useReplyCrud'
import useUser from '../../lib/useUser'
import Icon from '../common/Icon'
import BaseModal from '../common/modal/BaseModal'
import ConfirmModal from '../common/modal/ConfirmModal'
import AddToShowcase from '../common/popout/AddToShowcase'
import CopyButton from '../common/popout/CopyButton'
import FollowButton from '../common/popout/FollowButton'
import { Popout, PopoutContent, PopoutTrigger } from '../common/popout/Popout'
import PopoutButton from '../common/popout/PopoutButton'
import EditPost from '../forms/EditPost'
import ReportAction from '../profile/common/ReportAction'
import { useTranslation } from '../../lib/i18n'

const PostActions = ({
  groupId,
  item,
  parentSlug,
  parentType,
  type,
  disableReplyActions,
}) => {
  const id = parentSlug || item?.slug || item?.id
  const { popoutTriggerRef, popoutIsOpen } = usePopoutTriggerRef()
  const { user, isGroupManager } = useUser()
  const { t } = useTranslation()
  const shareLink = `https://${window.location.host}${useMemoizedPathFor(
    'post',
    id
  )}`
  const isAuthor = user?.id === item?.user?.id
  const isManager = isGroupManager(groupId)
  const { canParticipate } = useGroup(
    item.postable_type?.toLowerCase() === 'group' && popoutIsOpen
      ? item.postable?.slug
      : null
  )
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false)
  const [editVisible, setEditVisible] = useState(false)
  const [modalChangesWithInput, setModalChangesWithInput] = useState(false)
  const { deleteReply } = useReplyCrud(parentSlug)
  const { updatePost, deletePost } = usePostCrud()
  const deleteAction = parentSlug == null ? deletePost : deleteReply

  useLeavePageConfirmation(
    modalChangesWithInput && editVisible,
    t('common.discardChangesConfirmation')
  )

  const onDelete = async () => {
    try {
      await deleteAction({ slug: item.slug, type: parentType })
      if (window.location.pathname.includes('/posts/')) {
        router.replace('/')
      }
    } catch (error) {
      console.error('An unexpected error happened:', error)
    }
  }

  return (
    <Popout>
      <PopoutTrigger ref={popoutTriggerRef}>
        <div className="flex flex-row items-center -mr-1.5 -my-2 lg:-my-1">
          <Icon name="more" color="offBlack" />
        </div>
      </PopoutTrigger>
      <PopoutContent>
        {!isAuthor && (
          <ReportAction type={item?.type || type} id={item?.id} isPopout />
        )}
        {!disableReplyActions && (
          <>
            {!parentSlug && (
              <CopyButton copyable={shareLink} label={t('activity.copyLink')} />
            )}
            {!parentSlug && canParticipate && <FollowButton item={item} />}
            {!parentSlug && isManager && (
              <PopoutButton
                onClick={() =>
                  updatePost({
                    postable_type: item.postable_type,
                    postable_id: item.postable_id,
                    is_pinned: !item.is_pinned,
                    slug: item.slug,
                  })
                }
              >
                {item.is_pinned
                  ? t('activity.unpinPost')
                  : t('activity.pinPost')}
              </PopoutButton>
            )}
            <AddToShowcase
              id={item?.slug || item?.id}
              type={item?.type}
              visibility={item?.postable?.visibility}
              badges={item?.postable?.badges}
            />
            {isAuthor && (
              <>
                <PopoutButton onClick={() => setEditVisible(true)}>
                  {parentSlug
                    ? t('activity.editReply')
                    : t('activity.editPost')}
                </PopoutButton>
                <EditModal
                  isOpen={editVisible}
                  onRequestClose={() => setEditVisible(false)}
                  item={item}
                  parentSlug={parentSlug}
                  parentType={parentType}
                  modalChangesWithInput={modalChangesWithInput}
                  setModalChangesWithInput={setModalChangesWithInput}
                />
              </>
            )}
            {(isAuthor || isManager) && (
              <>
                <PopoutButton
                  className="text-topic-pink"
                  onClick={() => setDeleteConfirmVisible(true)}
                >
                  {parentSlug
                    ? t('activity.deleteReply')
                    : t('activity.deletePost')}
                </PopoutButton>
                <ConfirmModal
                  title={
                    parentSlug
                      ? t('activity.deleteReplyConfirm')
                      : t('activity.deletePostConfirm')
                  }
                  subtitle={
                    item?.postable?.name
                      ? t('activity.deleteItemConfirmationWithGroup', {
                          itemType: parentSlug ? 'reply' : 'post',
                          group: item?.postable?.name,
                        })
                      : t('activity.deleteItemConfirmation', {
                          itemType: parentSlug ? 'reply' : 'post',
                        })
                  }
                  actionLabel={t('ui.actions.delete')}
                  danger
                  cancelLabel={t('ui.actions.cancel')}
                  action={onDelete}
                  isOpen={deleteConfirmVisible}
                  onRequestClose={() => setDeleteConfirmVisible(false)}
                />
              </>
            )}
          </>
        )}
      </PopoutContent>
    </Popout>
  )
}

function EditModal({
  isOpen,
  onRequestClose,
  item,
  parentType,
  parentSlug,
  modalChangesWithInput,
  setModalChangesWithInput,
}) {
  const { t } = useTranslation()
  const handleCloseEditModal = async () => {
    if (modalChangesWithInput && isOpen) {
      const confirm = await window.confirm(
        t('common.discardChangesConfirmation')
      )
      if (confirm) {
        onRequestClose()
      }
    } else {
      onRequestClose()
    }
  }

  return (
    <BaseModal isOpen={isOpen} onRequestClose={handleCloseEditModal}>
      <EditPost
        item={item}
        toggleModal={onRequestClose}
        parentSlug={parentSlug}
        parentType={parentType}
        setModalChangesWithInput={setModalChangesWithInput}
      />
    </BaseModal>
  )
}

export default PostActions
